import React from "react";
import { Truck, ShieldCheck, Video, Clock, ArrowRight, Zap, Globe, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import SEO from "../components/SEO"; 

const info = [
  { icon: <Clock size={22} />, title: "07_Business_Days", desc: "Estimated Delivery" },
  { icon: <Truck size={22} />, title: "Premium_Tier", desc: "Priority Logistics" },
  { icon: <Video size={22} />, title: "Unboxing_Req", desc: "Verification Protocol" },
  { icon: <ShieldCheck size={22} />, title: "Encrypted", desc: "Vault Packaging" },
];

const sections = [
  {
    num: "01",
    title: "Logistics_Grid",
    text: "Once dispatched from our high-security vault, orders are delivered within 5–7 business days. We partner exclusively with tier-1 logistics providers like BlueDart and Delhivery to ensure that every archive asset remains protected during transit."
  },
  {
    num: "02",
    title: "Reversal_Protocol",
    text: "Given the limited nature of curated archive pieces, return requests must be initiated within 24 hours of delivery. A formal Return Authorization (RA) is mandatory; unauthorized returns will be rejected at our receiving facility."
  },
  {
    num: "03",
    title: "Visual_Evidence",
    text: "For all damage or authenticity claims, a continuous, unedited unboxing video is mandatory. The footage must clearly show the intact seal and the entire extraction process without pauses or cuts."
  }
];

export default function Shipping() {
  const { isDarkMode } = useTheme();

  return (
    <>
      <SEO 
        title="Logistics & Dispatch | Shipping Policy" 
        description="Official shipping and return protocols for THE_ARCHIVE. Information on asset transit timelines, unboxing mandates, and return authorizations."
        url="/shipping"
      />

      <section className={`min-h-screen transition-colors duration-500 py-32 px-6 relative overflow-hidden ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
        
        {/* Background Aesthetic Glows */}
        <div className={`fixed top-[-10%] right-[-10%] w-[50%] h-[50%] blur-[150px] rounded-full pointer-events-none ${isDarkMode ? 'bg-emerald-500/5' : 'bg-black/5'}`}></div>
        <div className={`fixed bottom-[-10%] left-[-10%] w-[40%] h-[40%] blur-[120px] rounded-full pointer-events-none ${isDarkMode ? 'bg-emerald-500/5' : 'bg-black/5'}`}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">

          {/* --- Header Section --- */}
          <div className="mb-24 text-left border-l-4 border-emerald-500 pl-8">
            <div className="flex items-center gap-3 mb-4">
               <Zap size={14} className="text-emerald-500 animate-pulse" />
               <span className="text-emerald-500 font-black text-[10px] tracking-[0.5em] uppercase">Security_Encrypted_Transit</span>
            </div>
            <h1 className="text-6xl md:text-9xl font-black tracking-tighter uppercase italic leading-[0.8]">
              Transit <span className={isDarkMode ? 'text-zinc-800' : 'text-zinc-200'}>& Logistics</span>
            </h1>
            <p className="mt-8 text-[11px] font-black uppercase tracking-[0.4em] text-zinc-500 max-w-xl">
              Official Protocol Documentation // Version 2.0.4 // Authorized Global Distribution Grid
            </p>
          </div>

          {/* --- Industrial Info Cards --- */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-24">
            {info.map((item, i) => (
              <div
                key={i}
                className={`p-10 rounded-[2.5rem] border backdrop-blur-3xl transition-all duration-500 ${
                  isDarkMode 
                  ? 'bg-zinc-950/50 border-white/5 shadow-2xl' 
                  : 'bg-zinc-50 border-black/5'
                }`}
              >
                <div className="flex justify-between items-start mb-8">
                   <div className="text-emerald-500 bg-emerald-500/10 p-3 rounded-2xl">
                      {item.icon}
                   </div>
                   <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_10px_#10b981]"></div>
                </div>
                <p className="text-[12px] font-black uppercase tracking-widest text-white mb-2">
                  {item.title}
                </p>
                <p className="text-[9px] text-zinc-500 uppercase font-black tracking-widest italic">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* --- Core Policy Sections --- */}
          <div className="grid md:grid-cols-3 gap-10 mb-24">
            {sections.map((item, i) => (
              <div
                key={i}
                className={`relative border rounded-[3.5rem] p-12 transition-all duration-700 ${
                  isDarkMode 
                  ? 'bg-zinc-900/10 border-white/5 shadow-[0_20px_50px_rgba(0,0,0,0.5)]' 
                  : 'bg-zinc-50 border-black/5 shadow-xl'
                }`}
              >
                {/* Number Badge */}
                <div className={`absolute -top-5 left-12 px-6 py-2 rounded-full font-black text-[10px] tracking-widest shadow-2xl ${
                  isDarkMode ? 'bg-emerald-500 text-black' : 'bg-black text-white'
                }`}>
                  SEC_{item.num}
                </div>

                <h3 className="text-2xl font-black uppercase tracking-tighter mb-8 italic text-emerald-500">
                  {item.title}
                </h3>

                <p className="text-[13px] leading-relaxed text-zinc-400 font-bold uppercase tracking-tight">
                  {item.text}
                </p>
              </div>
            ))}
          </div>

          {/* --- Return Authorization Terminal --- */}
          <div className={`relative overflow-hidden p-12 md:p-20 rounded-[4rem] border transition-all duration-500 ${
            isDarkMode ? 'bg-zinc-950 border-white/5 shadow-2xl' : 'bg-zinc-50 border-black/10'
          }`}>
            {/* Background Decorative Grid */}
            <div className="absolute inset-0 opacity-10 pointer-events-none" 
                 style={{ backgroundImage: `radial-gradient(${isDarkMode ? '#ffffff' : '#000000'} 1px, transparent 1px)`, backgroundSize: '30px 30px' }}>
            </div>

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-12">
              <div className="max-w-2xl">
                <div className="flex items-center gap-4 mb-8">
                   <div className="p-4 bg-emerald-500/10 rounded-3xl">
                      <Lock size={24} className="text-emerald-500" />
                   </div>
                   <div>
                      <h4 className="text-[11px] font-black uppercase tracking-[0.5em] text-emerald-500">
                        Asset_Reversal_Terminal
                      </h4>
                      <p className="text-[9px] text-zinc-600 font-black uppercase tracking-widest">Authorized Personnel Only</p>
                   </div>
                </div>

                <p className={`text-sm leading-relaxed mb-10 font-bold uppercase tracking-tight ${isDarkMode ? 'text-zinc-500' : 'text-zinc-600'}`}>
                  To initiate an archive reversal protocol, access the secure link below. 
                  Our technicians will review your claim metadata within 24 hours to authorize the return sequence.
                </p>

                <Link to="/contact">
                  <button className={`flex items-center gap-6 px-16 py-7 rounded-[2rem] font-black text-[11px] uppercase tracking-[0.4em] transition-all active:scale-95 shadow-[0_20px_40px_rgba(0,0,0,0.3)] ${
                    isDarkMode 
                    ? 'bg-emerald-500 text-black' 
                    : 'bg-black text-white'
                  }`}>
                    Initialize_Return <ArrowRight size={20} />
                  </button>
                </Link>
              </div>

              <div className="hidden lg:block">
                 <div className="w-64 h-64 border-2 border-dashed border-emerald-500/20 rounded-full flex items-center justify-center animate-[spin_20s_linear_infinite]">
                    <div className="w-48 h-48 border-2 border-emerald-500/40 rounded-full flex items-center justify-center animate-[spin_10s_linear_infinite_reverse]">
                       <Globe size={60} className="text-emerald-500 opacity-20" />
                    </div>
                 </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}